/**
 * API Interceptor for JWT Token Management
 * 
 * Provides automatic token refresh and session handling for fetch-based API calls.
 * Handles 401 errors by refreshing tokens and retrying failed requests.
 */

import { store } from '../app/store';
import { setUser, logout } from '../features/user/userSlice';
import { isServerBusyError, HIGH_TRAFFIC_MESSAGE } from './serverBusy';
import { readErrorBody } from './readErrorBody';
import { classifyError } from './errorTaxonomy';

/**
 * Attaches a description of the failure without altering the error itself.
 *
 * An earlier version of this also overwrote `error.message` with the friendly
 * copy, reasoning that the many components rendering `err.message` would all
 * improve for free. They did not. Roughly forty call sites do things like
 * `alert('Error updating order status: ' + error.message)`, so a write that
 * failed for an entirely unrelated reason started announcing "Terminal is
 * handling a burst of activity" — which is not merely unhelpful but wrong, and
 * hid the actual cause behind reassurance. That reached production.
 *
 * So `message` is left exactly as it was. Callers that want the friendly copy
 * read `error.classified.message` deliberately, which makes each such decision
 * visible at the call site instead of applying to everything at once.
 */
function describeForDisplay(error, response = null) {
  if (!error) return error;
  // A deliberate cancellation is not a failure and must never be rendered.
  if (error.aborted || error.name === 'RequestAbortedError') return error;
  if (error.classified) return error;

  try {
    error.classified = classifyError(error, response ? { response } : {});
  } catch (e) {
    // Describing a failure must never itself fail the request.
    console.error('[apiInterceptor] could not classify error:', e);
  }
  return error;
}

const REFRESH_TOKEN_URL = '/api/auth/token/refresh/';
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Track if a refresh is currently in progress to prevent multiple simultaneous refreshes
let isRefreshing = false;
// Queue of callbacks to call once refresh completes
let refreshSubscribers = [];
// Track if session expired popup has been shown to prevent duplicates
let isSessionExpiredPopupShown = false;

/**
 * Subscribe to refresh completion
 * @param {Function} callback - Function to call when refresh completes
 */
function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

/**
 * Call all subscribed callbacks after refresh completes
 * @param {string|null} newToken - The new access token, or null if refresh failed
 */
function onRefreshed(newToken) {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
}

/**
 * Clear all stored authentication data and trigger session expired popup
 */
function handleSessionExpired() {
  // Prevent duplicate popup triggers
  if (isSessionExpiredPopupShown) {
    console.log('[INTERCEPTOR] Session expired popup already shown, skipping');
    return;
  }
  
  console.log('[INTERCEPTOR] handleSessionExpired called - dispatching event');
  isSessionExpiredPopupShown = true;
  
  // Dispatch custom event to trigger global session expired popup
  window.dispatchEvent(new CustomEvent('sessionExpired'));
}

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<string|null>} - New access token or null if refresh failed
 */
async function refreshAccessToken() {
  const state = store.getState();
  const refreshToken = state.user.refresh || localStorage.getItem('refresh');
  
  if (!refreshToken) {
    console.warn('No refresh token available');
    return null;
  }
  
  try {
    // Use plain fetch (not intercepted) to avoid infinite loops
    const response = await fetch(`${BASE_URL}${REFRESH_TOKEN_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh: refreshToken,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorCode = errorData.error || errorData.code || 'TOKEN_EXPIRED';
      
      // Check for TOKEN_EXPIRED or INVALID_TOKEN error codes (both access and refresh token expiry)
      if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN') {
        console.error('Token expired or invalid, session ended');
      } else {
        console.error('Token refresh failed:', errorCode);
      }
      return null;
    }
    
    const data = await response.json();
    
    // Save new access token and refresh token
    // Backend returns fixed refresh token (same for 7 days from login)
    const newAccessToken = data.token || data.access;
    const newRefreshToken = data.refresh || refreshToken;
    
    if (newAccessToken) {
      // Always overwrite both tokens, even if refresh is the same
      localStorage.setItem('token', newAccessToken);
      localStorage.setItem('refresh', newRefreshToken);
      
      const currentState = store.getState();
      store.dispatch(setUser({
        token: newAccessToken,
        refresh: newRefreshToken,
        user_id: currentState.user.user_id,
        user_type: currentState.user.user_type,
        features: currentState.user.features,
        brand_onboarding_stage: currentState.user.brand_onboarding_stage,
      }));
      
      console.log('Token refreshed successfully and Redux store updated');
    }
    
    return newAccessToken;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
}

/**
 * Process queued requests after token refresh
 * @param {string|null} newToken - The new access token
 */
function processQueue(newToken) {
  onRefreshed(newToken);
}

/**
 * Create fetch options with authorization header
 * @param {object} options - Original fetch options
 * @returns {object} - Updated fetch options with auth header
 */
function withAuth(options = {}) {
  // Read token from Redux store first, fallback to localStorage
  const state = store.getState();
  const token = state.user.token || localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return {
    ...options,
    headers,
  };
}

/**
 * Intercept fetch responses and handle 401 errors with automatic token refresh
 * 
 * Usage: Wrap your fetch calls with this interceptor
 * 
 * Example:
 * const response = await apiInterceptor(
 *   fetch(`${BASE_URL}/api/endpoint`, withAuth({ method: 'GET' }))
 * );
 * 
 * @param {Promise} fetchPromise - The original fetch promise
 * @param {object} originalConfig - Original request config (for retry)
 * @returns {Promise<Response>} - The fetch response
 */
export async function apiInterceptor(fetchPromise, originalConfig = null) {
  console.log('[INTERCEPTOR] apiInterceptor called');
  try {
    const response = await fetchPromise;
    
    // If response is OK, return it directly
    if (response.ok) {
      return response;
    }
    
    // Handle 401 Unauthorized errors
    if (response.status === 401) {
      console.log('[INTERCEPTOR] 401 detected, attempting token refresh');
      
      // If not already refreshing, start the refresh process
      if (!isRefreshing) {
        isRefreshing = true;
        
        try {
          const newToken = await refreshAccessToken();
          
          if (newToken) {
            // Refresh succeeded, process queue with new token
            processQueue(newToken);
            console.log('[INTERCEPTOR] Token refresh succeeded, retrying request');
            
            // Retry the original request if we have the config
            if (originalConfig) {
              console.log('Retrying original request with new token');
              const retryResponse = await fetch(originalConfig.url, {
                ...originalConfig.options,
                headers: {
                  ...originalConfig.options.headers,
                  'Authorization': `Bearer ${newToken}`,
                },
              });
              
              isRefreshing = false;
              return retryResponse;
            } else {
              // No original config to retry - log error and throw
              console.error('No original request config available for retry');
              isRefreshing = false;
              const error = new Error('Session expired - unable to retry request');
              error.status = 401;
              error.data = { error: 'SESSION_EXPIRED', message: 'Unable to retry request' };
              throw error;
            }
          } else {
            // Refresh failed, notify all waiting requests BEFORE clearing tokens
            console.error('[INTERCEPTOR] Token refresh failed, showing session expired popup');
            processQueue(null); // This rejects all waiting requests
            handleSessionExpired();
            const error = new Error('Session expired');
            error.status = 401;
            error.data = { error: 'TOKEN_EXPIRED', message: 'Session expired, please log in again' };
            throw error;
          }
        } catch (refreshError) {
          // Refresh process failed - notify all waiting requests
          console.error('Refresh process failed:', refreshError);
          processQueue(null); // This rejects all waiting requests
          handleSessionExpired();
          const error = new Error('Session expired');
          error.status = 401;
          error.data = { error: 'TOKEN_EXPIRED', message: 'Session expired, please log in again' };
          throw error;
        } finally {
          isRefreshing = false;
        }
      } else {
        // Already refreshing, wait for it to complete
        console.log('Refresh in progress, waiting...');
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            if (newToken && originalConfig) {
              // Retry the original request with new token
              fetch(originalConfig.url, {
                ...originalConfig.options,
                headers: {
                  ...originalConfig.options.headers,
                  'Authorization': `Bearer ${newToken}`,
                },
              })
                .then(resolve)
                .catch(reject);
            } else {
              // Refresh failed or no config to retry
              const error = new Error('Session expired');
              error.status = 401;
              error.data = { error: 'TOKEN_EXPIRED', message: 'Session expired, please log in again' };
              reject(error);
            }
          });
        });
      }
    }
    
    // Handle 403 Forbidden errors (logout immediately)
    if (response.status === 403) {
      console.error('403 Forbidden - insufficient permissions');
      handleSessionExpired();
      const error = new Error('Access forbidden');
      error.status = 403;
      error.data = { error: 'FORBIDDEN', message: 'You do not have permission to access this resource' };
      throw error;
    }
    
    // 502/503/504 mean nginx could not get a timely answer out of the app — the
    // backend is saturated, not broken. These get a "try again shortly" message
    // instead of a raw status line, and are flagged so callers can tell the
    // difference without re-deriving it from the status.
    if (isServerBusyError(response.status)) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(HIGH_TRAFFIC_MESSAGE);
      error.status = response.status;
      error.isServerBusy = true;
      error.data = { ...errorData, error: 'SERVER_BUSY', message: HIGH_TRAFFIC_MESSAGE };
      throw error;
    }

    // For other error statuses, throw error with status and data
    if (!response.ok) {
      // Not `response.json()` unguarded: Django returns text/plain for uncaught
      // exceptions and nginx serves HTML for 502/503, so parsing throws on
      // exactly the failures most in need of explanation.
      const { body } = await readErrorBody(response);
      const errorData = body || {};
      const error = new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = errorData;
      throw describeForDisplay(error, response);
    }

    return response;
  } catch (error) {
    // Network errors or other failures - ensure error has status and data
    if (!error.status) {
      error.status = 0;
      error.data = { error: 'NETWORK_ERROR', message: error.message || 'Network error occurred' };
    }
    // A request the browser gave up on (connection dropped while the server was
    // overloaded) arrives here as a bare TypeError with no status — same cause,
    // same message.
    if (!error.isServerBusy && isServerBusyError(error)) {
      error.isServerBusy = true;
      error.data = { ...(error.data || {}), error: 'SERVER_BUSY' };
    }
    console.error('API request failed:', error);
    throw describeForDisplay(error);
  }
}

/**
 * Enhanced fetch wrapper that automatically includes auth and handles token refresh
 * 
 * Usage:
 * const response = await authenticatedFetch(`${BASE_URL}/api/endpoint`, {
 *   method: 'POST',
 *   body: JSON.stringify(data)
 * });
 * 
 * For file uploads with FormData:
 * const formData = new FormData();
 * formData.append('file', file);
 * const response = await authenticatedFetch(`${BASE_URL}/api/upload`, {
 *   method: 'POST',
 *   body: formData
 * });
 * (Don't set Content-Type header for FormData - browser will set it automatically)
 * 
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export async function authenticatedFetch(url, options = {}) {
  // Read token from Redux store first, fallback to localStorage
  const state = store.getState();
  const token = state.user.token || localStorage.getItem('token');
  const headers = {
    ...(options.headers || {}),
  };
  
  // Only set Content-Type to application/json if not already set and not FormData
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  // For FormData, don't set Content-Type - browser will set it with boundary
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    url,
    options: {
      ...options,
      headers,
    },
  };
  
  const fetchPromise = fetch(url, config.options);
  return apiInterceptor(fetchPromise, config);
}

/**
 * Helper to check if user is authenticated
 * @returns {boolean} - True if user has a token
 */
export function isAuthenticated() {
  const state = store.getState();
  return !!(state.user.token || localStorage.getItem('token'));
}

/**
 * Helper to get current access token
 * @returns {string|null} - Current access token or null
 */
export function getCurrentToken() {
  const state = store.getState();
  return state.user.token || localStorage.getItem('token');
}

/**
 * Helper to get current refresh token
 * @returns {string|null} - Current refresh token or null
 */
export function getRefreshToken() {
  const state = store.getState();
  return state.user.refresh || localStorage.getItem('refresh');
}

const apiInterceptorModule = {
  apiInterceptor,
  authenticatedFetch,
  isAuthenticated,
  getCurrentToken,
  getRefreshToken,
  withAuth,
};

/**
 * Helper function to handle 401 errors in raw fetch calls
 * Use this in catch blocks or after checking response.status in raw fetch calls
 * 
 * Usage:
 * if (response.status === 401) {
 *   handleRawFetch401();
 *   return;
 * }
 */
export function handleRawFetch401() {
  // Prevent duplicate popup triggers
  if (isSessionExpiredPopupShown) {
    console.log('[INTERCEPTOR] handleRawFetch401 - popup already shown, skipping');
    return;
  }
  
  console.log('[INTERCEPTOR] handleRawFetch401 called - dispatching session expired event');
  handleSessionExpired();
}

// Listen for reset event from App.js when user clicks "Go to Login"
if (typeof window !== 'undefined') {
  window.addEventListener('resetSessionExpiredFlag', () => {
    console.log('[INTERCEPTOR] Resetting session expired popup flag');
    isSessionExpiredPopupShown = false;
  });
}

export default apiInterceptorModule;
